<template>
  <button :class="['v-button', status, uiType]" v-bind="$attrs" :aria-label="title">
    <spinner v-if="status === 'loading'" :width="20" :height="20" />
    <icon v-else-if="convertedIconName" :name="convertedIconName" class="icon" :size="iconSize" />
    <span v-if="title && uiType !== 'icon-only'" :class="['title', { 'sr-only': status !== 'normal' }]">
      {{ title }}
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import Icon from "@/components/atoms/material-icon.vue";
import Spinner from "@/components/atoms/spinner.vue";
export type ButtonStatus = "normal" | "loading" | "success" | "error";

export default defineComponent({
  name: "component-v-button",
  components: {
    Icon,
    Spinner
  },
  inheritAttrs: false,
  props: {
    uiType: {
      type: String,
      validator: (value: string) => {
        return ["primary", "secondary", "tertiary", "text", "danger", "transparent", "icon-only"].includes(value);
      },
      required: true
    },
    title: { type: String, required: false },
    iconName: { type: String, default: undefined, required: false },
    iconSize: { type: Number, default: 24 },
    status: {
      type: String,
      validator: (value: string) => {
        return ["normal", "loading", "success", "error"].includes(value);
      },
      default: "normal",
      required: false
    }
  },

  setup(props) {
    const computedProps = {
      convertedIconName: computed(() => {
        switch (props.status) {
          case "loading":
            return "";
          case "success":
            return "done";
          case "error":
            return "error";
          default:
            return props.iconName;
        }
      })
    };

    return {
      ...computedProps
    };
  }
});
</script>

<style lang="scss" scoped>
$hoveredOpacity: 0.7;
$disabledOpacity: 0.35;

button {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  min-height: 44px;
  height: auto;
  border-radius: 100px;

  &.loading,
  &.error,
  &.success {
    opacity: $disabledOpacity;
    pointer-events: none;
  }

  // status
  .icon + .title {
    margin-left: 8px;
  }

  &:active,
  &:hover {
    opacity: $hoveredOpacity;
  }
  &:disabled {
    opacity: $disabledOpacity;
    cursor: not-allowed;
  }

  // type
  &.primary {
    color: white;
    background-color: $fun-text;
  }

  &.secondary {
    color: $fun-text;
    background-color: white;
    border: solid 2px $fun-text;
  }

  &.tertiary {
    color: $fun-text;
    background-color: white;
    border: solid 1px $fun-text;
  }

  &.text {
    color: $fun-text;
    background-color: transparent;
    border: 0;
  }

  &.danger {
    color: white;
    background-color: $important;
    &:active,
    &:hover {
      background-color: rgba($important, 0.6);
    }
    &:disabled {
      opacity: $disabledOpacity;
      cursor: not-allowed;
    }
  }

  &.transparent {
    color: $fun-text;
    background-color: transparent;
  }
}
</style>
